import { useCustomMutation } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { useCallback } from 'react';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';
import { COMMAND_FIELDS } from './model';

/**
 * E35 · the Assessments commands from the portal - RecordExternalAssessment, CreateAssessment (in session) and PublishInstrument (the
 * backoffice) - each ONE deliberate act (a button), posted with `errorNotification: false` so the screen reports the outcome itself:
 * a CODED refusal (`assessments.errors.*`, already in the therapist's language) is shown verbatim; anything else is the screen's own
 * copy plus Sentry.
 */
export type AssessmentsOperation = 'recordExternalAssessment' | 'createAssessmentInSession' | 'publishInstrument';

const INPUT_TYPE: Record<AssessmentsOperation, string> = {
  recordExternalAssessment: 'RecordExternalAssessmentInput!',
  createAssessmentInSession: 'CreateAssessmentInSessionInput!',
  publishInstrument: 'PublishInstrumentInput!',
};

export type AssessmentsCommandResult =
  | { ok: true; id: string; recorded: boolean }
  | { ok: false; code: string | undefined; message: string };

export const useAssessmentsCommand = () => {
  const { mutateAsync, isLoading } = useCustomMutation<{ id: string; recorded: boolean }>();

  const run = useCallback(
    async (
      operation: AssessmentsOperation,
      input: Record<string, unknown>,
      fallback: string,
    ): Promise<AssessmentsCommandResult> => {
      try {
        const res = await mutateAsync({
          url: '',
          method: 'post',
          values: {},
          errorNotification: false,
          successNotification: false,
          meta: {
            operation,
            fields: COMMAND_FIELDS,
            variables: { input: { name: 'input', type: INPUT_TYPE[operation], value: input } },
          },
        });
        const data = res?.data;
        if (!data?.id) return { ok: false, code: undefined, message: fallback };
        return { ok: true, id: data.id, recorded: !!data.recorded };
      } catch (error) {
        const info = graphqlErrorInfo(error);
        if (info.code && info.message) return { ok: false, code: info.code, message: info.message };
        Sentry.captureException(error);
        return { ok: false, code: info.code, message: fallback };
      }
    },
    [mutateAsync],
  );

  return { run, isLoading };
};
